import React, { memo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { bindActionCreators } from 'redux';
import dayjs from 'dayjs';

import { TimerProvider } from '../contexts/TimerContext';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import * as authActionCreators from '../actions/auth';
import AppRouting from '../routing/AppRouting';
import Popup from './PopupWrapper';
import ActivityDetector from './ActivityDetector';
import DialPadPusher from './DialPadPusher';
import ApiErrorDisplay from './ApiErrorWrapper';
import RuntimeErrorDisplay from './RuntimeErrorWrapper';
import UnloadWrapper from './UnloadWrapper';
import parseJwt from '../utils/token';
import { getCode } from '../middleware/activate';
import config from '../config';
import { AppVersionCheck } from './VersionCheck';

const INTERVAL_TOKEN_REFRESH = 60 * 1000; // 1 minute

const AppContainer = ({ user, checkAuth, refreshToken }) => {
  const refreshIntervalRef = useRef(null);

  const initRefreshTokenByTimer = useCallback(() => {
    refreshIntervalRef.current = setInterval(() => {
      const parsedToken = parseJwt(getCode());

      if (parsedToken) {
        const now = dayjs();
        const expiration = dayjs(parsedToken.exp * 1000);

        if (expiration.diff(now, 'minute') <= 5) {
          refreshToken();
        }
      }
    }, INTERVAL_TOKEN_REFRESH);
  }, [refreshToken]);

  useEffect(() => {
    checkAuth({
      afterSuccess: () => {
        initRefreshTokenByTimer();
      },
    });

    return () => {
      clearInterval(refreshIntervalRef.current);
    };
  }, []);

  const content = user ? (
    <RuntimeErrorDisplay global onDevOnly>
      <AppVersionCheck />
      <ActivityDetector>
        <DialPadPusher>
          <TimerProvider>
            <AppRouting />
          </TimerProvider>
        </DialPadPusher>
      </ActivityDetector>
    </RuntimeErrorDisplay>
  ) : null;

  return (
    <div className="full-height">
      <Helmet
        defaultTitle=""
        htmlAttributes={{ lang: 'en' }}
        link={[{ rel: 'manifest', href: '/assets/manifest.json' }]}
        meta={[
          { charset: 'urt8' },
          { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
          { name: 'description', content: '' },
          {
            name: 'viewport',
            content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0',
          },
          { name: 'msapplication-tap-highlight', content: 'no' },
          { name: 'mobile-web-app-capable', content: 'yes' },
          { name: 'application-name', content: config.appName },
          { name: 'apple-mobile-web-app-capable', content: 'yes' },
          { name: 'apple-mobile-web-app-status-bar-style', content: 'black' },
          { name: 'apple-mobile-web-app-title', content: config.appName },
          { name: 'msapplication-TileColor', content: config.color },
          { name: 'theme-color', content: config.color },
        ]}
        titleTemplate="%s"
      />
      <UnloadWrapper>
        <Popup>
          <ApiErrorDisplay>{content}</ApiErrorDisplay>
        </Popup>
      </UnloadWrapper>
    </div>
  );
};

AppContainer.propTypes = {
  checkAuth: PropTypes.func.isRequired,
  refreshToken: PropTypes.func.isRequired,
};

const ConnectedApp = connect(
  state => ({
    user: state.auth.get('user', null),
    location: state.router.location,
  }),
  dispatch => bindActionCreators(authActionCreators, dispatch),
  mergeProps,
  connectOptions,
)(memo(AppContainer));

ConnectedApp.fetchData = ({ store }) => store.dispatch(authActionCreators.checkAuth({}));

export default ConnectedApp;
